'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import {
  trackSubscriptionConversion,
  trackPurchase,
  createEcommerceItem,
} from '@/lib/tracking'
import type { SubscriptionDetails } from '@/lib/tracking/types'
import { GOOGLE_ADS_CONVERSION_LABEL } from '@/lib/constant'

interface SubscriptionDetailsResponse {
  subscription_id: string
  plan: 'basic' | 'premium' | 'platinum'
  credits: number
  domains: number
  billing_interval: 'monthly' | 'yearly'
  amount: number
  currency: string
  email: string
  conversion_tracked: boolean
}

/**
 * Payment Success Tracker Component
 * Handles Google Ads conversion tracking and GA4 purchase event after successful payment
 *
 * Usage:
 * <PaymentSuccessTracker />
 *
 * This component should be included on any page that users land on after successful payment,
 * typically with ?payment_success=true in the URL
 */
export function PaymentSuccessTracker() {
  const searchParams = useSearchParams()
  const [tracked, setTracked] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const trackConversion = async () => {
      // Check if this is a payment success page
      const paymentSuccess = searchParams?.get('payment_success')
      if (paymentSuccess !== 'true') {
        return
      }

      // Prevent duplicate tracking
      if (tracked) {
        return
      }

      try {
        // Try to get sessionId or subscriptionId from URL parameters if available
        const sessionId =
          searchParams?.get('session_id') || searchParams?.get('sessionId')
        const subscriptionId =
          searchParams?.get('subscription_id') ||
          searchParams?.get('subscriptionId')

        // Build API URL with query parameters if available
        // If no parameters are provided, API will automatically find the most recent subscription
        const apiUrl = new URL(
          '/api/subscription/details',
          window.location.origin,
        )
        if (sessionId) {
          apiUrl.searchParams.set('sessionId', sessionId)
        }
        if (subscriptionId) {
          apiUrl.searchParams.set('subscriptionId', subscriptionId)
        }

        // Fetch subscription details from API
        const response = await fetch(apiUrl.toString())

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            errorData.error || 'Failed to fetch subscription details',
          )
        }

        const data: SubscriptionDetailsResponse = await response.json()

        // Skip if already tracked
        if (data.conversion_tracked) {
          console.log('Conversion already tracked, skipping')
          setTracked(true)
          return
        }

        // Prepare subscription details
        const subscriptionDetails: SubscriptionDetails = {
          plan: data.plan,
          credits: data.credits,
          domains: data.domains,
          billing_interval: data.billing_interval,
          amount: data.amount,
          currency: data.currency,
        }

        // Track Google Ads conversion
        if (GOOGLE_ADS_CONVERSION_LABEL) {
          await trackSubscriptionConversion(
            GOOGLE_ADS_CONVERSION_LABEL,
            data.subscription_id,
            data.amount,
            data.email,
          )
          console.log('Google Ads conversion tracked successfully')
        } else {
          console.warn(
            'Google Ads conversion label not configured. Set NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_LABEL environment variable.',
          )
        }

        // Send GA4 purchase event on client to preserve client/session attribution
        const gaItem = createEcommerceItem(subscriptionDetails)
        trackPurchase(
          data.subscription_id,
          data.amount,
          [gaItem],
          undefined, // coupon
        )

        setTracked(true)
        console.log('Payment conversion tracking completed successfully')
      } catch (err) {
        console.error('Failed to track conversion:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    trackConversion()
  }, [searchParams, tracked])

  // This component doesn't render anything visible
  // It only handles tracking in the background
  if (error) {
    // Only log errors in development
    if (process.env.NODE_ENV === 'development') {
      return (
        <div style={{ display: 'none' }} data-tracking-error={error}>
          Tracking Error: {error}
        </div>
      )
    }
  }

  return null
}
